/**
 * The editor reducer: every editing transition as a pure function.
 *
 * `(state, action) -> state`, with no I/O and no DOM. The canvas translates
 * pointer and keyboard events into these actions (task 16); saving writes the
 * resulting document through `PUT /draft` (task 17). Nothing here talks to the
 * server, which is what lets every rule be tested directly.
 *
 * Three disciplines run through it:
 *
 * - **Structural sharing.** An edit replaces only the objects it touches, so
 *   the scene builder's per-element memo recomputes only what changed
 *   (requirement 14.4).
 * - **Snapshots for undo.** Each committed edit pushes the previous document.
 *   A drag is one step, however many pointer moves it took.
 * - **Refuse with a reason.** An edit the rules forbid leaves the document
 *   untouched and sets a notice saying why (requirements 6.3, 6.4, D20).
 */
import {
  LABEL_SIDES,
  regionApplications,
  servesKind,
  type LabelSide,
  type DesignDocument,
  type Direction,
  type EdgeDocument,
  type NodeDocument,
  type TemplateDocument,
  type TextNoteDocument,
  MAX_NOTE_TEXT,
  NOTE_SIZE,
} from "../design/document";
import { plainText } from "../design/noteText";
import { buildScene, DEFAULT_VIEW, formatLength } from "../scene";
import { partition } from "../scene/consumable";
import { ALL_ANNOTATIONS, lineShown, type Bounds, type Point } from "../scene/types";
import { copySelection, pasteContent } from "./clipboard";
import { drawnScene, isMountable, mountOnDrop, mountOnLine, slideMounted, unmountFrom } from "./mounts";
import { startingValues } from "./defaults";
import { allocateIdentifier, usedIdentifiers } from "./identifiers";
import { orientElements, type OrientOperation } from "./orient";
import {
  checkConnection,
  checkEndpoint,
  checkReconnect,
  checkTemplateSwap,
  findNode,
  findPort,
  lockingInstance,
  validTargets,
} from "./rules";
import { clampZoom } from "./viewport";
import {
  defaultAirLine,
  defaultConnector,
  defaultJunction,
  UNDO_LIMIT,
  type EditorAction,
  type EditorState,
  type NoteSizeValue,
  type Notice,
  type Refusal,
} from "./state";

/**
 * Edge template parameters stored in `EdgeDocument.hydraulic` rather than
 * `properties`, mirroring HYDRAULIC_KEYS in core/library/instantiate.py.
 * Hydraulic geometry is what physics reads (invariant 4), so it is stored
 * whole, from the template's defaults, never from the drawn path.
 */
export const HYDRAULIC_KEYS = ["length", "width", "depth", "cross_section"] as const;

type HydraulicKey = (typeof HYDRAULIC_KEYS)[number];

function isHydraulicKey(key: string): key is HydraulicKey {
  return (HYDRAULIC_KEYS as readonly string[]).includes(key);
}

// --- Small helpers ------------------------------------------------------------

function snap(value: number, state: EditorState): number {
  const { spacing, snap: enabled } = state.grid;
  return enabled && spacing > 0 ? Math.round(value / spacing) * spacing : value;
}

/** Refs the selection may name: nodes, edges and live block instances. */
function liveRefs(design: DesignDocument): Set<string> {
  return new Set([
    ...(design.nodes ?? []).map((node) => node.local_id),
    ...(design.edges ?? []).map((edge) => edge.local_id),
    ...(design.group_instances ?? [])
      .filter((instance) => !instance.detached)
      .map((instance) => instance.local_id),
    ...(design.text_notes ?? []).map((note) => note.local_id),
  ]);
}

/**
 * What an isolating view shows (SceneAnnotations.isolate), or null when it
 * hides nothing: no wash, not isolating, or no boundary to have sides.
 */
function isolatedSide(state: EditorState, annotations = state.annotations): ReadonlySet<string> | null {
  if (!annotations.isolate || annotations.wash === "none") return null;
  const side = partition(state.document, state.library.templates)[annotations.wash];
  return side.size > 0 ? side : null;
}

function isNote(state: EditorState, ref: string): boolean {
  return (state.document.text_notes ?? []).some((note) => note.local_id === ref);
}

function pruneSelection(selection: ReadonlySet<string>, design: DesignDocument): ReadonlySet<string> {
  const live = liveRefs(design);
  const kept = [...selection].filter((ref) => live.has(ref));
  return kept.length === selection.size ? selection : new Set(kept);
}

/** Commit an edit: push the previous document for undo, and clear redo. */
function commit(
  state: EditorState,
  document: DesignDocument,
  changes: Partial<EditorState> = {},
): EditorState {
  if (document === state.document) {
    return { ...state, notice: null, ...changes };
  }
  return {
    ...state,
    document,
    undo: [...state.undo, state.document].slice(-UNDO_LIMIT),
    redo: [],
    notice: null,
    ...changes,
  };
}

function refused(state: EditorState, refusal: Refusal, changes: Partial<EditorState> = {}): EditorState {
  return { ...state, notice: { kind: "refused", ...refusal }, ...changes };
}

/** Nodes the selection moves or turns: selected nodes and members of selected blocks. */
function selectedNodeIds(state: EditorState): Set<string> {
  const ids = new Set<string>();
  const nodes = new Set((state.document.nodes ?? []).map((node) => node.local_id));
  for (const ref of state.selection) {
    if (nodes.has(ref)) ids.add(ref);
  }
  for (const instance of state.document.group_instances ?? []) {
    if (!instance.detached && state.selection.has(instance.local_id)) {
      for (const member of instance.member_local_ids ?? []) {
        if (nodes.has(member)) ids.add(member);
      }
    }
  }
  return ids;
}

// --- Placing and connecting ---------------------------------------------------

/**
 * Splice a just-placed node into a line (dropping a component onto a channel).
 *
 * The line keeps its identifier and runs from its start into one of the new
 * node's fluidic ports; a new line continues from another to the original end.
 * The pair is the one nearest each end, so a valve dropped on a left-to-right
 * line takes it in at its left port and out at its right.
 *
 * Hydraulic length is split evenly, so the total is conserved. Nothing drawn
 * decides it (invariant 4): an even split is the one default that does not
 * pretend to know, and the notice says so.
 */
function spliceIntoEdge(
  state: EditorState,
  design: DesignDocument,
  node: NodeDocument,
  edgeRef: string,
): { document: DesignDocument; notice: Notice | null; newEdge: string | null } {
  const edge = design.edges?.find((candidate) => candidate.local_id === edgeRef);
  const unchanged = (message: string) => ({
    document: design,
    notice: { kind: "info" as const, message, refs: [node.local_id, edgeRef] },
    newEdge: null,
  });
  if (!edge) return unchanged(`${edgeRef} is no longer in the design, so ${node.local_id} was placed on its own`);
  const instance = lockingInstance(design, edgeRef);
  if (instance) {
    return unchanged(`${edgeRef} is locked in ${instance.local_id}, so ${node.local_id} was placed beside it, not into it`);
  }
  const fluidic = (node.ports ?? []).filter((port) => (port.kind ?? "fluidic") === "fluidic");
  if (fluidic.length < 2) {
    return unchanged(
      `a ${node.role.replace(/_/g, " ")} has ${fluidic.length === 1 ? "one fluidic port" : "no fluidic ports"}, ` +
        `so ${node.local_id} was placed beside ${edgeRef}, not into it`,
    );
  }

  // Where things are drawn, only to choose which port faces which end.
  const scene = buildScene(design, state.library, [], { ...DEFAULT_VIEW, collapsed: state.collapsed });
  const sceneEdge = scene.edges.find((candidate) => candidate.ref === edgeRef);
  const sceneNode = scene.nodes.find((candidate) => candidate.ref === node.local_id);
  if (!sceneEdge || !sceneNode) return unchanged(`${edgeRef} is not drawn, so ${node.local_id} was placed on its own`);
  const positionOf = (portId: string) =>
    sceneNode.ports.find((port) => port.ref === `${node.local_id}.${portId}`)!.position;
  const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
  let best: { into: string; outOf: string; cost: number } | null = null;
  for (const into of fluidic) {
    for (const outOf of fluidic) {
      if (into === outOf) continue;
      const cost =
        distance(positionOf(into.local_id), sceneEdge.from.point) + distance(positionOf(outOf.local_id), sceneEdge.to.point);
      // Strictly better only, so ties keep the template's declared order.
      if (!best || cost < best.cost - 1e-9) best = { into: into.local_id, outOf: outOf.local_id, cost };
    }
  }
  const into = `${node.local_id}.${best!.into}`;
  const outOf = `${node.local_id}.${best!.outOf}`;

  const length = edge.hydraulic?.length;
  const half = typeof length === "number" ? length / 2 : length;
  const hydraulic = { ...(edge.hydraulic ?? {}), ...(typeof half === "number" ? { length: half } : {}) };
  const template = edge.template_version_id ? state.library.templates[edge.template_version_id] : undefined;
  const newId = allocateIdentifier(template?.role ?? edge.type ?? "channel", usedIdentifiers(design, state.reserved));
  // Both parts route afresh: the old bends described a line that no longer exists.
  const { schematic_waypoints: _bends, ...rest } = edge;
  void _bends;
  const first: EdgeDocument = { ...rest, to_port: into, hydraulic };
  const second: EdgeDocument = { ...rest, local_id: newId, from_port: outOf, to_port: edge.to_port, hydraulic };

  const edges = design.edges!.flatMap((candidate) => (candidate === edge ? [first, second] : [candidate]));
  const lengthNote =
    typeof length === "number"
      ? ` Its ${formatLength(length)} was split evenly, ${formatLength(length / 2)} each; adjust either in the inspector.`
      : "";
  return {
    document: { ...design, edges },
    notice: {
      kind: "info",
      message: `Inserted ${node.local_id} into ${edgeRef}: ${edgeRef} now ends at ${into}, and ${newId} continues from ${outOf}.${lengthNote}`,
      refs: [node.local_id, edgeRef, newId],
    },
    newEdge: newId,
  };
}

/**
 * A node from a template version, at a snapped position. Requirement 6.1: the
 * ports the template declares, with the author's default for which are
 * external. Parameters hold overrides only, and the template supplies the
 * rest; a placement default (D36) that differs from the template's is one.
 */
function newNode(
  state: EditorState,
  design: DesignDocument,
  templateVersionId: string,
  template: TemplateDocument,
  at: Point,
  rotation = 0,
): NodeDocument {
  return {
    local_id: allocateIdentifier(template.role, usedIdentifiers(design, state.reserved)),
    role: template.role as NodeDocument["role"],
    label: "",
    template_version_id: templateVersionId,
    parameters: nodeOverrides(state, design, templateVersionId, template),
    ...(template.state_set ? { state_set: [...template.state_set] } : {}),
    ...(template.state_transitions ? { state_transitions: template.state_transitions.map((t) => ({ ...t })) } : {}),
    ...(template.initial_state ? { initial_state: template.initial_state } : {}),
    ports: (template.ports ?? []).map((port) => ({
      local_id: port.local_id,
      name: port.name ?? "",
      kind: port.kind ?? "fluidic",
      is_external: port.external_by_default ?? false,
      ...(port.interface ? { interface: port.interface } : {}),
    })),
    schematic_position: { x: snap(at.x, state), y: snap(at.y, state), ...(rotation ? { rotation } : {}) },
  };
}

/** Equal as a stored parameter: numbers to within rounding, anything else exactly. */
function sameDefault(a: unknown, b: unknown): boolean {
  if (typeof a === "number" && typeof b === "number") {
    return a === b || Math.abs(a - b) <= 1e-12 * Math.max(Math.abs(a), Math.abs(b));
  }
  return a === b;
}

/** A new node's overrides: the placement defaults (D36) that differ from its template's. */
function nodeOverrides(
  state: EditorState,
  design: DesignDocument,
  templateVersionId: string,
  template: TemplateDocument,
): Record<string, unknown> {
  const starting = startingValues(template, state.templateEntities[templateVersionId], design, state.userDefaults);
  const overrides: Record<string, unknown> = {};
  for (const parameter of template.parameters ?? []) {
    const value = starting[parameter.key];
    if (value !== undefined && !sameDefault(value, parameter.default)) overrides[parameter.key] = value;
  }
  return overrides;
}

/**
 * A channel between two ports from the connector's template. Requirement
 * 6.5: hydraulic properties from the template's defaults, never from the
 * drawn path — the drag's geometry is not even an input here.
 */
/**
 * Whether a port carries gas: a vent, or an instrument connection whose
 * interface is pneumatic or vacuum (D25). A line to one is an air line (D30).
 */
function carriesAir(design: DesignDocument, ref: string): boolean {
  const port = findPort(design, ref)?.port;
  if (!port) return false;
  return port.kind === "vent" || (port.kind === "instrument" && (port.interface === "pneumatic" || port.interface === "vacuum"));
}

function newEdge(
  state: EditorState,
  design: DesignDocument,
  from: string,
  to: string,
  /** The line this one branches from, whose kind a branch takes. */
  like?: EdgeDocument,
): { edge: EdgeDocument } | { refusal: Refusal } {
  // An air line where the ends say so (D30), whatever the connector is set
  // to; the connector's own choice otherwise. Never the other way: a
  // designer who chose an air line for two fluidic ports gets one.
  const air =
    state.airLineTemplateId !== null &&
    (like?.type === "air_line" || carriesAir(design, from) || carriesAir(design, to));
  const templateId = air ? state.airLineTemplateId : state.connectorTemplateId;
  const template = templateId ? state.library.templates[templateId] : undefined;
  if (!templateId || !template || template.element !== "edge") {
    return { refusal: { code: "no_connector_template", message: "choose a channel type for the connector", refs: [] } };
  }
  // The template's defaults, with the design's and this person's over them
  // (D36). Hydraulic geometry is stored whole; anything else only where it
  // differs from the template, as an override.
  const starting = startingValues(template, state.templateEntities[templateId], design, state.userDefaults);
  const hydraulic: Record<string, unknown> = {};
  const properties: Record<string, unknown> = {};
  for (const parameter of template.parameters ?? []) {
    const value = starting[parameter.key];
    if (value === undefined) continue;
    if (isHydraulicKey(parameter.key)) hydraulic[parameter.key] = value;
    else if (!sameDefault(value, parameter.default)) properties[parameter.key] = value;
  }
  return {
    edge: {
      local_id: allocateIdentifier(template.role, usedIdentifiers(design, state.reserved)),
      from_port: from,
      to_port: to,
      type: template.role as EdgeDocument["type"],
      template_version_id: templateId,
      hydraulic,
      properties,
    },
  };
}

function placeNode(state: EditorState, templateVersionId: string, at: Point, intoEdge?: string): EditorState {
  const template = state.library.templates[templateVersionId];
  if (!template) {
    return refused(state, {
      code: "unknown_template",
      message: `template version ${templateVersionId} is not loaded`,
      refs: [],
    });
  }
  if ((template.element ?? "node") !== "node") {
    return refused(state, {
      code: "not_a_node_template",
      message: `a ${template.role} is created by connecting two ports, not by placing it`,
      refs: [],
    });
  }
  const node = newNode(state, state.document, templateVersionId, template, at);
  const localId = node.local_id;
  const placed: DesignDocument = { ...state.document, nodes: [...(state.document.nodes ?? []), node] };
  if (!intoEdge) {
    return commit(state, placed, { selection: new Set([localId]) });
  }
  // On a tubing set, a part that sits on a tube is mounted on it (D40), not
  // spliced in: the tube runs on unbroken.
  if (state.designKind === "tubing_set" && isMountable(node, template)) {
    const mounted = mountOnDrop(placed, state.library, node, intoEdge, at);
    if (mounted) {
      return {
        ...commit(state, mounted.document, { selection: new Set([localId]) }),
        notice: { kind: "info", message: mounted.message, refs: [localId, intoEdge] },
      };
    }
  }
  // Placing and splicing are one edit, so one undo step removes both.
  const aligned = alignToLine(state, state.document, node, intoEdge, at);
  const turnedPlaced: DesignDocument =
    aligned === node ? placed : { ...placed, nodes: placed.nodes!.map((candidate) => (candidate === node ? aligned : candidate)) };
  const spliced = spliceIntoEdge(state, turnedPlaced, aligned, intoEdge);
  return { ...commit(state, spliced.document, { selection: new Set([localId]) }), notice: spliced.notice };
}

function beginConnect(state: EditorState, port: string): EditorState {
  if (state.tool !== "connect") {
    return state;
  }
  const refusal = checkEndpoint(state.document, state.library, port);
  if (refusal) {
    return refused(state, refusal, { drag: null });
  }
  return {
    ...state,
    notice: null,
    drag: {
      kind: "connect",
      from: port,
      hover: null,
      validTargets: validTargets(state.document, state.library, port),
    },
  };
}

function completeConnect(state: EditorState, target: string | null): EditorState {
  const drag = state.drag;
  if (drag?.kind !== "connect") {
    return state;
  }
  if (target === null) {
    // Dropped on empty canvas: nothing to connect, and nothing to explain.
    return { ...state, drag: null };
  }
  const refusal = checkConnection(state.document, state.library, drag.from, target);
  if (refusal) {
    return refused(state, refusal, { drag: null });
  }
  const made = newEdge(state, state.document, drag.from, target);
  if ("refusal" in made) return refused(state, made.refusal, { drag: null });
  return commit(
    state,
    { ...state.document, edges: [...(state.document.edges ?? []), made.edge] },
    { drag: null, selection: new Set([made.edge.local_id]) },
  );
}

// --- Branching ------------------------------------------------------------------

const QUARTER: Record<Direction, number> = { north: 0, east: 1, south: 2, west: 3 };
const DIRECTIONS: readonly Direction[] = ["north", "east", "south", "west"];

function turned(direction: Direction, quarterTurns: number): Direction {
  return DIRECTIONS[(QUARTER[direction] + quarterTurns + 4) % 4]!;
}

/** The segment of a drawn path nearest a point, as its axis. */
function segmentAxis(path: readonly Point[], at: Point): "horizontal" | "vertical" {
  let best = { distance: Infinity, horizontal: true };
  for (let index = 1; index < path.length; index += 1) {
    const a = path[index - 1]!;
    const b = path[index]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length = dx * dx + dy * dy;
    const t = length === 0 ? 0 : Math.max(0, Math.min(1, ((at.x - a.x) * dx + (at.y - a.y) * dy) / length));
    const distance = Math.hypot(a.x + t * dx - at.x, a.y + t * dy - at.y);
    if (distance < best.distance) best = { distance, horizontal: Math.abs(dx) >= Math.abs(dy) };
  }
  return best.horizontal ? "horizontal" : "vertical";
}

/**
 * The quarter turns that sit a junction on a line: two fluidic ports along
 * the line's axis and one facing `toward`. Drawing only (invariant 4); it
 * just keeps the branch from doubling back on itself.
 */
function junctionTurns(template: TemplateDocument, axis: "horizontal" | "vertical", toward: Direction | null): number {
  const directions = (template.ports ?? [])
    .filter((port) => (port.kind ?? "fluidic") === "fluidic")
    .map((port) => port.anchor?.direction ?? "east");
  const along: Direction[] = axis === "horizontal" ? ["west", "east"] : ["north", "south"];
  const fits = (turns: number, wanted: Direction | null) => {
    const facing = directions.map((direction) => turned(direction, turns));
    return along.every((direction) => facing.includes(direction)) && (wanted === null || facing.includes(wanted));
  };
  for (const wanted of [toward, null]) {
    for (const turns of [0, 1, 2, 3]) if (fits(turns, wanted)) return turns;
  }
  return 0;
}

function quarterTurnsOf(node: NodeDocument): number {
  return ((Math.round((node.schematic_position?.rotation ?? 0) / (Math.PI / 2)) % 4) + 4) % 4;
}

/**
 * Turn a component about to be spliced into a line so the line passes through
 * it: two fluidic ports along the line's axis. Left alone when it already
 * sits that way, so a deliberate orientation survives. Drawing only
 * (invariant 4).
 */
function alignToLine(state: EditorState, lines: DesignDocument, node: NodeDocument, edgeRef: string, at: Point): NodeDocument {
  const template = node.template_version_id ? state.library.templates[node.template_version_id] : undefined;
  if (!template) return node;
  // `lines` is the design before the component arrived: lines route around
  // symbols, so with it already on the line the line would bend away.
  const scene = buildScene(lines, state.library, [], { ...DEFAULT_VIEW, collapsed: state.collapsed });
  const drawn = scene.edges.find((candidate) => candidate.ref === edgeRef);
  if (!drawn) return node;
  const axis = segmentAxis(drawn.path, at);
  const current = quarterTurnsOf(node);
  const along: Direction[] = axis === "horizontal" ? ["west", "east"] : ["north", "south"];
  const facing = (template.ports ?? [])
    .filter((port) => (port.kind ?? "fluidic") === "fluidic")
    .map((port) => turned(port.anchor?.direction ?? "east", current));
  if (along.every((direction) => facing.includes(direction))) return node;
  const turns = junctionTurns(template, axis, null);
  const position = node.schematic_position ?? { x: at.x, y: at.y };
  return { ...node, schematic_position: { ...position, rotation: turns * (Math.PI / 2) } };
}

/**
 * Splice a component that was moved onto a line (END_MOVE with `intoEdge`):
 * centred on the line, turned to take it, then spliced as a palette drop is.
 */
function spliceMoved(state: EditorState, edgeRef: string, at: Point): { document: DesignDocument; notice: Notice | null } {
  const design = state.document;
  const ref = [...state.selection][0];
  const node = ref ? findNode(design, ref) : undefined;
  if (!node || state.selection.size !== 1) return { document: design, notice: null };
  // A free part that sits on a tube, dropped on a tubing set's line: mounted (D40).
  const template = node.template_version_id ? state.library.templates[node.template_version_id] : undefined;
  if (state.designKind === "tubing_set" && isMountable(node, template)) {
    const before = state.drag?.kind === "move" ? state.drag.origin : design;
    const mounted = mountOnDrop(before, state.library, findNode(before, node.local_id) ?? node, edgeRef, at);
    if (mounted) return { document: mounted.document, notice: { kind: "info", message: mounted.message, refs: [node.local_id, edgeRef] } };
  }
  const centred: NodeDocument = {
    ...node,
    schematic_position: { ...(node.schematic_position ?? { x: 0, y: 0 }), x: snap(at.x, state), y: snap(at.y, state) },
  };
  const before = state.drag?.kind === "move" ? state.drag.origin : design;
  const aligned = alignToLine(state, before, centred, edgeRef, at);
  const placed: DesignDocument = {
    ...design,
    nodes: design.nodes!.map((candidate) => (candidate === node ? aligned : candidate)),
  };
  const spliced = spliceIntoEdge(state, placed, aligned, edgeRef);
  // Refused (a locked line, too few ports): the move stands, with the reason.
  return spliced.newEdge === null ? { document: design, notice: spliced.notice } : spliced;
}

function branchEdge(state: EditorState, edgeRef: string, from?: string, at?: Point): EditorState {
  const design = state.document;
  const edge = design.edges?.find((candidate) => candidate.local_id === edgeRef);
  if (!edge) return refused(state, { code: "unknown_element", message: `${edgeRef} is not in this design`, refs: [edgeRef] }, { drag: null });
  const instance = lockingInstance(design, edgeRef);
  if (instance) return refused(state, lockedRefusal(edgeRef, instance.local_id, "branch off it"), { drag: null });
  const templateId = state.junctionTemplateId;
  const template = templateId ? state.library.templates[templateId] : undefined;
  if (!templateId || !template) {
    return refused(
      state,
      { code: "no_junction_template", message: "the library has no channel junction to branch with", refs: [edgeRef] },
      { drag: null },
    );
  }
  if (from) {
    const refusal = checkEndpoint(design, state.library, from);
    if (refusal) return refused(state, refusal, { drag: null });
  }

  const scene = buildScene(design, state.library, [], { ...DEFAULT_VIEW, collapsed: state.collapsed });
  const drawn = scene.edges.find((candidate) => candidate.ref === edgeRef);
  if (!drawn) {
    return refused(state, { code: "unknown_element", message: `${edgeRef} is not drawn`, refs: [edgeRef] }, { drag: null });
  }
  const point = at ?? drawn.labelPosition;
  const axis = segmentAxis(drawn.path, point);
  const source = from
    ? [...scene.nodes, ...scene.blocks].flatMap((element) => element.ports).find((port) => port.ref === from)?.position
    : undefined;
  const toward: Direction | null = source
    ? axis === "horizontal"
      ? source.y < point.y
        ? "north"
        : "south"
      : source.x < point.x
        ? "west"
        : "east"
    : null;
  const turns = junctionTurns(template, axis, toward);

  const junction = newNode(state, design, templateId, template, point, turns * (Math.PI / 2));
  const placed: DesignDocument = { ...design, nodes: [...(design.nodes ?? []), junction] };
  const spliced = spliceIntoEdge(state, placed, junction, edgeRef);
  if (spliced.newEdge === null) return refused(state, { code: "unknown_element", message: spliced.notice?.message ?? "", refs: [edgeRef] }, { drag: null });

  // The port the splice left free is the branch.
  const used = new Set(
    (spliced.document.edges ?? []).flatMap((candidate) => [candidate.from_port, candidate.to_port]),
  );
  const free = (junction.ports ?? []).find(
    (port) => (port.kind ?? "fluidic") === "fluidic" && !used.has(`${junction.local_id}.${port.local_id}`),
  );
  if (!from || !free) {
    return {
      ...commit(state, spliced.document, { drag: null, selection: new Set([junction.local_id]) }),
      notice: spliced.notice,
    };
  }
  const branchPort = `${junction.local_id}.${free.local_id}`;
  const refusal = checkConnection(spliced.document, state.library, from, branchPort);
  if (refusal) return refused(state, refusal, { drag: null });
  const made = newEdge(state, spliced.document, from, branchPort, edge);
  if ("refusal" in made) return refused(state, made.refusal, { drag: null });
  const document = { ...spliced.document, edges: [...(spliced.document.edges ?? []), made.edge] };
  return {
    ...commit(state, document, { drag: null, selection: new Set([made.edge.local_id]) }),
    notice: {
      kind: "info",
      message: `Branched ${edgeRef} at ${junction.local_id} and connected ${from} to it with ${made.edge.local_id}. ${spliced.notice?.message ?? ""}`.trim(),
      refs: [junction.local_id, edgeRef, spliced.newEdge, made.edge.local_id],
    },
  };
}

// --- Selection ----------------------------------------------------------------

function select(state: EditorState, refs: readonly string[], mode: "replace" | "extend" | "toggle"): EditorState {
  const live = liveRefs(state.document);
  const valid = refs.filter((ref) => live.has(ref));
  let selection: Set<string>;
  if (mode === "replace") {
    selection = new Set(valid);
  } else {
    selection = new Set(state.selection);
    for (const ref of valid) {
      if (mode === "toggle" && selection.has(ref)) selection.delete(ref);
      else selection.add(ref);
    }
  }
  return { ...state, selection, notice: null };
}

function contains(box: Bounds, point: Point): boolean {
  return point.x >= box.minX && point.x <= box.maxX && point.y >= box.minY && point.y <= box.maxY;
}

/**
 * Box selection hit-tests against the scene itself, so what is selected is
 * exactly what is drawn inside the box — including collapsed blocks as one
 * symbol. Elements must lie wholly inside, as in draw.io.
 */
function boxSelect(state: EditorState, box: Bounds, mode: "replace" | "extend"): EditorState {
  const scene = buildScene(state.document, state.library, [], {
    ...DEFAULT_VIEW,
    collapsed: state.collapsed,
    // What an isolated view hides cannot be boxed either.
    annotations: { ...ALL_ANNOTATIONS, wash: state.annotations.wash, isolate: state.annotations.isolate },
  });
  const inside = (centre: Point, width: number, height: number, rotation: number) => {
    const sideways = Math.round(rotation / (Math.PI / 2)) % 2 !== 0;
    const halfW = (sideways ? height : width) / 2;
    const halfH = (sideways ? width : height) / 2;
    return (
      contains(box, { x: centre.x - halfW, y: centre.y - halfH }) &&
      contains(box, { x: centre.x + halfW, y: centre.y + halfH })
    );
  };
  const hits = [
    ...scene.nodes
      .filter((node) => inside(node.centre, node.size.width, node.size.height, node.rotation))
      .map((node) => node.ref),
    ...scene.edges.filter((edge) => edge.path.every((point) => contains(box, point))).map((edge) => edge.ref),
    ...scene.blocks
      .filter((block) => inside(block.centre, block.size.width, block.size.height, block.collapsed ? block.rotation : 0))
      .map((block) => block.ref),
    // Hidden notes are not drawn, so a box cannot take them.
    ...(state.annotations.notes ? scene.notes : [])
      .filter(
        (note) =>
          contains(box, note.position) &&
          contains(box, { x: note.position.x + note.size.width, y: note.position.y + note.size.height }),
      )
      .map((note) => note.ref),
  ];
  return select(state, hits, mode === "extend" ? "extend" : "replace");
}

// --- Moving -------------------------------------------------------------------

/**
 * Move the selection by an offset, snapped so the arrangement moves as a unit:
 * the first moved node lands on the grid and every other node moves by the
 * same amount, so relative positions are never disturbed by snapping.
 */
/** The node at each end of an edge. */
function endNodes(edge: EdgeDocument): [string, string] {
  return [edge.from_port.split(".")[0]!, edge.to_port.split(".")[0]!];
}

/**
 * Bends of edges with both ends in `ids` move with them, so a group moved or
 * turned as a whole keeps its lines' shape. Edges with one end outside keep
 * their bends where they are, as in draw.io.
 */
export function carryWaypoints(
  edges: readonly EdgeDocument[] | undefined,
  ids: ReadonlySet<string>,
  transform: (point: Point) => Point,
): EdgeDocument[] | undefined {
  if (!edges) return edges;
  let changed = false;
  const next = edges.map((edge) => {
    const [a, b] = endNodes(edge);
    if (!edge.schematic_waypoints?.length || !ids.has(a) || !ids.has(b)) return edge;
    changed = true;
    return { ...edge, schematic_waypoints: edge.schematic_waypoints.map(transform) };
  });
  return changed ? next : (edges as EdgeDocument[]);
}

/**
 * Collapsed block centres in a document, as drawn: once per document and set
 * of collapsed blocks, since a drag moves from one origin many times.
 */
const collapsedCentres = new WeakMap<DesignDocument, WeakMap<ReadonlySet<string>, Map<string, Point>>>();

function collapsedCentresOf(state: EditorState, base: DesignDocument): Map<string, Point> {
  if (state.collapsed.size === 0) return new Map();
  let byView = collapsedCentres.get(base);
  if (!byView) {
    byView = new WeakMap();
    collapsedCentres.set(base, byView);
  }
  const cached = byView.get(state.collapsed);
  if (cached) return cached;
  const scene = buildScene(base, state.library, [], {
    ...DEFAULT_VIEW,
    collapsed: state.collapsed,
    grid: { spacing: state.grid.spacing, visible: state.grid.visible },
  });
  const centres = new Map(scene.blocks.filter((block) => block.collapsed).map((block) => [block.ref, block.centre]));
  byView.set(state.collapsed, centres);
  return centres;
}

/**
 * Move the selection. A collapsed block moves its symbol only, recorded as
 * its collapsed position: its members stay where they are drawn expanded, so
 * the block keeps both arrangements. Everything else moves its nodes, and
 * the bends of lines between them.
 */
function moveNodes(state: EditorState, base: DesignDocument, dx: number, dy: number): DesignDocument {
  const collapsedSelected = (base.group_instances ?? []).filter(
    (instance) => !instance.detached && state.selection.has(instance.local_id) && state.collapsed.has(instance.local_id),
  );
  const heldBack = new Set(collapsedSelected.flatMap((instance) => instance.member_local_ids ?? []));
  const ids = new Set([...selectedNodeIds(state)].filter((id) => !heldBack.has(id) || state.selection.has(id)));
  // Mounted parts slide along their tubes (D40); everything else moves freely.
  // One whose tube moves as a whole with it (both ends selected) rides along instead.
  const movesWhole = (edgeRef: string) => {
    const edge = base.edges?.find((candidate) => candidate.local_id === edgeRef);
    return Boolean(edge && ids.has(edge.from_port.split(".")[0]!) && ids.has(edge.to_port.split(".")[0]!));
  };
  const mountedIds = (base.nodes ?? []).filter((node) => node.mount && ids.has(node.local_id)).map((node) => node);
  for (const node of mountedIds) ids.delete(node.local_id);
  const mountedMoving = new Set(mountedIds.filter((node) => !movesWhole(node.mount!.edge)).map((node) => node.local_id));
  if (mountedMoving.size > 0) {
    base = slideMounted(base, state.library, mountedMoving, dx, dy);
    if (ids.size === 0 && collapsedSelected.length === 0 && !(base.text_notes ?? []).some((note) => state.selection.has(note.local_id))) {
      return base;
    }
  }
  const nodes = base.nodes ?? [];
  const primary = nodes.find((node) => ids.has(node.local_id));
  const centres = collapsedSelected.length > 0 ? collapsedCentresOf(state, base) : new Map<string, Point>();
  // Notes selected, and notes attached to a component that moves, move too.
  const notes = base.text_notes ?? [];
  const movingNotes = new Set(
    notes
      .filter((note) => state.selection.has(note.local_id) || (note.anchor && ids.has(note.anchor)))
      .map((note) => note.local_id),
  );
  const origin =
    primary?.schematic_position ??
    (collapsedSelected[0] ? centres.get(collapsedSelected[0].local_id) : undefined) ??
    notes.find((note) => movingNotes.has(note.local_id))?.position;
  if (!origin) return base;
  const stepX = snap(origin.x + dx, state) - origin.x;
  const stepY = snap(origin.y + dy, state) - origin.y;
  if (stepX === 0 && stepY === 0) return base;
  const edges = carryWaypoints(base.edges, ids, (point) => ({ x: point.x + stepX, y: point.y + stepY }));
  const instances =
    collapsedSelected.length === 0
      ? base.group_instances
      : base.group_instances!.map((instance) => {
          const centre = centres.get(instance.local_id);
          if (!collapsedSelected.includes(instance) || !centre) return instance;
          return { ...instance, collapsed_position: { x: centre.x + stepX, y: centre.y + stepY } };
        });
  return {
    ...base,
    ...(edges !== base.edges ? { edges } : {}),
    ...(instances !== base.group_instances ? { group_instances: instances } : {}),
    ...(movingNotes.size > 0
      ? {
          text_notes: notes.map((note) =>
            movingNotes.has(note.local_id)
              ? { ...note, position: { x: note.position.x + stepX, y: note.position.y + stepY } }
              : note,
          ),
        }
      : {}),
    nodes: nodes.map((node) => {
      if (!ids.has(node.local_id)) return node;
      // Moving is presentation (invariant 4, requirement 6.8), so it is
      // permitted on locked block members: arranging is not editing (D20).
      const position = node.schematic_position ?? { x: 0, y: 0 };
      return { ...node, schematic_position: { ...position, x: position.x + stepX, y: position.y + stepY } };
    }),
  };
}

// --- Orientation --------------------------------------------------------------

function orientSelection(state: EditorState, operation: OrientOperation): EditorState {
  const result = orientElements(state.document, state.selection, operation, {
    library: state.library,
    snap: state.grid.snap ? state.grid.spacing : null,
    collapsedCentres: collapsedCentresOf(state, state.document),
  });
  return result.nodes.length === 0 && result.document === state.document ? state : commit(state, result.document);
}

// --- Pasting ------------------------------------------------------------------

/**
 * The document with each mounted part's position where it is drawn on its
 * tube (D40), so a copy of one without its tube lands where it was seen.
 */
function withDrawnPositions(state: EditorState): DesignDocument {
  const design = state.document;
  if (!(design.nodes ?? []).some((node) => node.mount)) return design;
  const scene = drawnScene(design, state.library);
  return {
    ...design,
    nodes: design.nodes!.map((node) => {
      const drawn = node.mount ? scene.nodes.find((candidate) => candidate.ref === node.local_id) : undefined;
      return drawn
        ? { ...node, schematic_position: { ...(node.schematic_position ?? {}), x: drawn.centre.x, y: drawn.centre.y } }
        : node;
    }),
  };
}

function paste(state: EditorState, at?: Point): EditorState {
  const content = state.clipboard;
  if (!content) return state;
  const positions = [
    ...content.nodes.map((node) => node.schematic_position ?? { x: 0, y: 0 }),
    ...(content.notes ?? []).map((note) => note.position),
  ];
  const centre = {
    x: (Math.min(...positions.map((p) => p.x)) + Math.max(...positions.map((p) => p.x))) / 2,
    y: (Math.min(...positions.map((p) => p.y)) + Math.max(...positions.map((p) => p.y))) / 2,
  };
  // Offsets are whole grid steps so a snapped copy stays snapped.
  const step = state.grid.spacing > 0 ? state.grid.spacing : 16;
  const cascade = (content.pastes + 1) * 2 * step;
  const offset = at
    ? { x: snap(at.x - centre.x, state), y: snap(at.y - centre.y, state) }
    : { x: cascade, y: cascade };
  const pasted = pasteContent(state.document, content, offset, usedIdentifiers(state.document, state.reserved));
  const count = pasted.refs.length;
  return {
    ...commit(state, pasted.document, { selection: new Set(pasted.refs), drag: null }),
    clipboard: at ? content : { ...content, pastes: content.pastes + 1 },
    notice: {
      kind: "info",
      message:
        `Pasted ${count === 1 ? pasted.refs[0] : `${count} elements`}.` +
        (content.leftBehind > 0
          ? ` ${content.leftBehind} line${content.leftBehind === 1 ? "" : "s"} to components outside the copy stayed with the original.`
          : ""),
      refs: pasted.refs,
    },
  };
}

// --- Deleting -----------------------------------------------------------------

function deleteSelection(state: EditorState): EditorState {
  const design = state.document;
  if (state.selection.size === 0) return state;

  const removed = new Set<string>();
  const instances = (design.group_instances ?? []).filter(
    (instance) => !instance.detached && state.selection.has(instance.local_id),
  );
  // Deleting a block removes its record and every member.
  for (const instance of instances) {
    removed.add(instance.local_id);
    for (const member of instance.member_local_ids ?? []) removed.add(member);
  }

  // D20: a member of a block that is not itself being deleted is locked.
  const locked: string[] = [];
  for (const ref of state.selection) {
    if (removed.has(ref)) continue;
    const instance = lockingInstance(design, ref);
    if (instance) {
      locked.push(ref);
      if (!locked.includes(instance.local_id)) locked.push(instance.local_id);
    }
  }
  if (locked.length > 0) {
    const members = locked.filter((ref) => !(design.group_instances ?? []).some((i) => i.local_id === ref));
    return refused(state, {
      code: "locked_block_member",
      message: `${members.join(", ")} ${members.length === 1 ? "is" : "are"} locked in a block; detach it to delete members`,
      refs: locked,
    });
  }

  const nodeIds = new Set((design.nodes ?? []).map((node) => node.local_id));
  for (const ref of state.selection) {
    if (nodeIds.has(ref) || (design.edges ?? []).some((edge) => edge.local_id === ref)) removed.add(ref);
  }

  // Requirement 6.6: edges attached to a removed port go with it, and are
  // reported. Edges the designer selected, or that belonged to a deleted
  // block, are not news.
  const goneNode = (ref: string) => removed.has(ref.split(".")[0]!);
  const cascaded: string[] = [];
  for (const edge of design.edges ?? []) {
    if (removed.has(edge.local_id)) continue;
    if (goneNode(edge.from_port) || goneNode(edge.to_port)) {
      removed.add(edge.local_id);
      cascaded.push(edge.local_id);
    }
  }

  // Treatment applications are not deleted with their targets: the targets
  // become unresolvable, which the checker reports (requirement 10.4). The
  // notice says so now rather than leaving it to the next check.
  const orphanedApplications = regionApplications(design)
    .filter((application) => (application.targets ?? []).some((target) => removed.has(target.split(".")[0]!)))
    .map((application) => application.local_id);

  // A note goes when it is selected. One attached to a component that goes
  // stays where it is, detached: what someone wrote is not lost as a side
  // effect of deleting what it was written about.
  const notes = design.text_notes ?? [];
  const notesChanged = notes.some((note) => state.selection.has(note.local_id) || (note.anchor && removed.has(note.anchor)));
  const keptNotes = notesChanged
    ? notes
        .filter((note) => !state.selection.has(note.local_id))
        .map((note) => {
          if (!note.anchor || !removed.has(note.anchor)) return note;
          const { anchor: _anchor, ...detached } = note;
          void _anchor;
          return detached;
        })
    : notes;

  const document: DesignDocument = {
    ...design,
    ...(notesChanged ? { text_notes: keptNotes } : {}),
    // A part mounted on a tube that goes stays, where it was drawn (D40).
    nodes: unmountFrom(
      design,
      state.library,
      removed,
      (design.nodes ?? []).filter((node) => !removed.has(node.local_id)),
    ),
    edges: (design.edges ?? []).filter((edge) => !removed.has(edge.local_id)),
    group_instances: (design.group_instances ?? []).filter((instance) => !removed.has(instance.local_id)),
    // Deleting does not retire (D29). Publishing retires what the version
    // before had and this one lacks; a name that only ever lived in drafts
    // was never citable. Retiring here made an undo of the deletion, once
    // autosaved, a document the server refuses. `reserved` keeps a published
    // name from going to a new element in the meantime.
  };

  let notice: Notice | null = null;
  if (cascaded.length > 0 || orphanedApplications.length > 0) {
    const parts: string[] = [];
    if (cascaded.length > 0) {
      parts.push(`also removed ${cascaded.join(", ")}, which connected to what was deleted`);
    }
    if (orphanedApplications.length > 0) {
      parts.push(
        `${orphanedApplications.join(", ")} now target${orphanedApplications.length === 1 ? "s" : ""} removed elements`,
      );
    }
    notice = { kind: "removed", message: parts.join("; "), refs: cascaded, orphanedApplications };
  }
  const next = commit(state, document, { selection: new Set() });
  return { ...next, notice };
}

// --- Parameters ---------------------------------------------------------------

function editParameter(state: EditorState, ref: string, key: string, value: unknown): EditorState {
  const result = withParameter(state, state.document, ref, key, value);
  return "refusal" in result ? refused(state, result.refusal) : commit(state, result.document);
}

/**
 * One parameter set on several elements at once, as one edit: undone in one
 * step, and refused whole if any element refuses it (a locked member, a
 * template without the parameter). Each takes the value as an override, or
 * loses its override where the value is its own template's default.
 */
function editParameters(state: EditorState, refs: readonly string[], key: string, value: unknown): EditorState {
  let document = state.document;
  for (const ref of refs) {
    const element = findNode(document, ref) ?? document.edges?.find((candidate) => candidate.local_id === ref);
    const templateId = element?.template_version_id;
    const declared = templateId ? state.library.templates[templateId]?.parameters?.find((parameter) => parameter.key === key) : undefined;
    const own = declared && isDefault(value, declared.default) ? null : value;
    const result = withParameter(state, document, ref, key, own);
    if ("refusal" in result) return refused(state, result.refusal);
    document = result.document;
  }
  return commit(state, document);
}

/** Whether a value is a template's default: numbers within float noise, anything else exactly. */
function isDefault(value: unknown, fallback: unknown): boolean {
  if (typeof value === "number" && typeof fallback === "number") {
    return Math.abs(value - fallback) <= 1e-9 * Math.max(Math.abs(value), Math.abs(fallback));
  }
  return value !== null && value !== undefined && value === fallback;
}

function withParameter(
  state: EditorState,
  design: DesignDocument,
  ref: string,
  key: string,
  value: unknown,
): { document: DesignDocument } | { refusal: Refusal } {
  const node = findNode(design, ref);
  const edge = node ? undefined : design.edges?.find((candidate) => candidate.local_id === ref);
  if (!node && !edge) {
    return { refusal: { code: "unknown_element", message: `${ref} is not in this design`, refs: [ref] } };
  }
  const instance = lockingInstance(design, ref);
  if (instance) {
    // D20: parameters describe the physical thing, which the lock protects.
    return { refusal: lockedRefusal(ref, instance.local_id, "change parameters") };
  }
  const templateId = (node ?? edge)!.template_version_id;
  const template = templateId ? state.library.templates[templateId] : undefined;
  const declaration = template?.parameters?.find((parameter) => parameter.key === key);
  if (template && !declaration) {
    return { refusal: { code: "unknown_parameter", message: `${template.role} has no parameter ${key}`, refs: [ref] } };
  }
  const clearing = value === null || value === undefined;

  if (node) {
    const parameters = { ...(node.parameters ?? {}) };
    if (clearing) delete parameters[key];
    else parameters[key] = value;
    return {
      document: { ...design, nodes: design.nodes!.map((candidate) => (candidate === node ? { ...node, parameters } : candidate)) },
    };
  }

  const current = edge!;
  let updated: EdgeDocument;
  if (isHydraulicKey(key)) {
    // Hydraulic values are stored whole, so clearing restores the default
    // rather than leaving the channel without a dimension.
    const hydraulic = { ...(current.hydraulic ?? {}) } as Record<string, unknown>;
    const restored = clearing ? declaration?.default : value;
    if (restored === undefined || restored === null) delete hydraulic[key];
    else hydraulic[key] = restored;
    updated = { ...current, hydraulic };
  } else {
    const properties = { ...(current.properties ?? {}) };
    if (clearing) delete properties[key];
    else properties[key] = value;
    updated = { ...current, properties };
  }
  return { document: { ...design, edges: design.edges!.map((candidate) => (candidate === current ? updated : candidate)) } };
}

function lockedRefusal(ref: string, instance: string, what: string): Refusal {
  return {
    code: "locked_block_member",
    message: `${ref} is locked in ${instance}; detach it to ${what}`,
    refs: [ref, instance],
  };
}

function setEdgeTemplate(state: EditorState, ref: string, templateVersionId: string): EditorState {
  const design = state.document;
  const edge = design.edges?.find((candidate) => candidate.local_id === ref);
  if (!edge) {
    return refused(state, { code: "unknown_element", message: `${ref} is not a channel in this design`, refs: [ref] });
  }
  const instance = lockingInstance(design, ref);
  if (instance) return refused(state, lockedRefusal(ref, instance.local_id, "change its type"));
  const template = state.library.templates[templateVersionId];
  if (!template || template.element !== "edge") {
    return refused(state, { code: "unknown_template", message: `${templateVersionId} is not a channel template`, refs: [] });
  }
  if (edge.template_version_id === templateVersionId) return state;
  // Geometry the designer set is kept where the new template declares it;
  // anything it declares that the channel lacks comes from its defaults, and
  // anything it does not declare goes, or the checker reports it as an
  // undeclared property. Non-hydraulic properties belong to the old
  // template's model (groove cycles), so they go too.
  const declared = new Set((template.parameters ?? []).map((parameter) => parameter.key));
  const hydraulic: Record<string, unknown> = Object.fromEntries(
    Object.entries(edge.hydraulic ?? {}).filter(([key]) => declared.has(key)),
  );
  for (const parameter of template.parameters ?? []) {
    if (isHydraulicKey(parameter.key) && hydraulic[parameter.key] == null && parameter.default != null) {
      hydraulic[parameter.key] = parameter.default;
    }
  }
  const updated: EdgeDocument = {
    ...edge,
    type: template.role as EdgeDocument["type"],
    template_version_id: templateVersionId,
    hydraulic,
    properties: {},
  };
  return commit(state, { ...design, edges: design.edges!.map((candidate) => (candidate === edge ? updated : candidate)) });
}

function setEdgeWaypoints(state: EditorState, ref: string, waypoints: readonly Point[]): EditorState {
  const design = state.document;
  const edge = design.edges?.find((candidate) => candidate.local_id === ref);
  if (!edge) {
    return refused(state, { code: "unknown_element", message: `${ref} is not a channel in this design`, refs: [ref] });
  }
  if (!waypoints.every((point) => Number.isFinite(point.x) && Number.isFinite(point.y))) return state;
  const current = edge.schematic_waypoints ?? [];
  const same =
    current.length === waypoints.length &&
    current.every((point, i) => point.x === waypoints[i]!.x && point.y === waypoints[i]!.y);
  if (same) return state;
  // Presentation only: allowed on locked block members, like moving them.
  const updated: EdgeDocument = { ...edge, schematic_waypoints: waypoints.map(({ x, y }) => ({ x, y })) };
  if (waypoints.length === 0) delete updated.schematic_waypoints;
  const document = { ...design, edges: design.edges!.map((candidate) => (candidate === edge ? updated : candidate)) };
  // Mid-drag, the drag is the undo step; END_MOVE records it.
  return state.drag?.kind === "move" ? { ...state, document } : commit(state, document);
}

function reconnectEdge(state: EditorState, ref: string, end: "from" | "to", port: string): EditorState {
  const design = state.document;
  const edge = design.edges?.find((candidate) => candidate.local_id === ref);
  if (edge && (end === "from" ? edge.from_port : edge.to_port) === port) return state;
  const refusal = checkReconnect(design, state.library, ref, end, port);
  if (refusal) return refused(state, refusal);
  const updated: EdgeDocument = { ...edge!, ...(end === "from" ? { from_port: port } : { to_port: port }) };
  return commit(state, { ...design, edges: design.edges!.map((candidate) => (candidate === edge ? updated : candidate)) });
}

/** Longest label kept; a schematic label is a name, not a note. */
export const MAX_LABEL_LENGTH = 120;

// --- Notes ------------------------------------------------------------------

/** What a note keeps of what was typed: its line breaks, without blank edges. */
function noteText(text: string): string {
  return text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.replace(/\s+$/, ""))
    .join("\n")
    .replace(/^\n+|\n+$/g, "")
    .slice(0, MAX_NOTE_TEXT);
}

/** A size the server accepts, in whole scene units; null for one that is not a size. */
function noteSize(size: NoteSizeValue | undefined): NoteSizeValue | null {
  if (!size || !Number.isFinite(size.width) || !Number.isFinite(size.height)) return null;
  const clamp = (value: number, min: number) => Math.min(NOTE_SIZE.max, Math.max(min, Math.round(value)));
  return { width: clamp(size.width, NOTE_SIZE.minWidth), height: clamp(size.height, NOTE_SIZE.minHeight) };
}

/** Whether a note's text says anything once its markup is set aside. */
const saysSomething = (text: string) => plainText(text).trim().length > 0;

function addNote(
  state: EditorState,
  at: Point,
  text: string,
  anchor?: string | null,
  requested?: NoteSizeValue,
): EditorState {
  const cleaned = noteText(text);
  if (!saysSomething(cleaned)) return state;
  const size = noteSize(requested);
  const design = state.document;
  const local_id = allocateIdentifier("note", usedIdentifiers(design, state.reserved));
  const attached = anchor && findNode(design, anchor) ? anchor : null;
  const note: TextNoteDocument = {
    local_id,
    text: cleaned,
    position: { x: snap(at.x, state), y: snap(at.y, state) },
    ...(attached ? { anchor: attached } : {}),
    ...(size ? { size } : {}),
  };
  return commit(
    state,
    { ...design, text_notes: [...(design.text_notes ?? []), note] },
    { selection: new Set([local_id]), tool: "select" },
  );
}

function setNoteText(state: EditorState, ref: string, text: string, requested?: NoteSizeValue): EditorState {
  const design = state.document;
  const notes = design.text_notes ?? [];
  const note = notes.find((candidate) => candidate.local_id === ref);
  if (!note) return refused(state, { code: "unknown_element", message: `${ref} is not a note in this design`, refs: [ref] });
  const cleaned = noteText(text);
  if (!saysSomething(cleaned)) {
    const selection = new Set([...state.selection].filter((selected) => selected !== ref));
    return commit(state, { ...design, text_notes: notes.filter((candidate) => candidate !== note) }, { selection });
  }
  const size = noteSize(requested) ?? note.size ?? null;
  const sameSize = (note.size?.width ?? null) === (size?.width ?? null) && (note.size?.height ?? null) === (size?.height ?? null);
  if (cleaned === note.text && sameSize) return state;
  return commit(state, {
    ...design,
    text_notes: notes.map((candidate) =>
      candidate === note ? { ...note, text: cleaned, ...(size ? { size } : {}) } : candidate,
    ),
  });
}

function setNoteSize(state: EditorState, ref: string, requested: NoteSizeValue): EditorState {
  const design = state.document;
  const notes = design.text_notes ?? [];
  const note = notes.find((candidate) => candidate.local_id === ref);
  if (!note) return refused(state, { code: "unknown_element", message: `${ref} is not a note in this design`, refs: [ref] });
  const size = noteSize(requested);
  if (!size || (note.size?.width === size.width && note.size?.height === size.height)) return state;
  const document = { ...design, text_notes: notes.map((candidate) => (candidate === note ? { ...note, size } : candidate)) };
  // Mid-drag, the drag is the undo step; END_MOVE records it.
  return state.drag?.kind === "move" ? { ...state, document } : commit(state, document);
}

function setNoteAnchor(state: EditorState, ref: string, anchor: string | null): EditorState {
  const design = state.document;
  const notes = design.text_notes ?? [];
  const note = notes.find((candidate) => candidate.local_id === ref);
  if (!note) return refused(state, { code: "unknown_element", message: `${ref} is not a note in this design`, refs: [ref] });
  if (anchor !== null && !findNode(design, anchor)) {
    return refused(state, { code: "unknown_element", message: `${anchor} is not a component in this design`, refs: [ref] });
  }
  if ((note.anchor ?? null) === anchor) return state;
  const { anchor: _previous, ...rest } = note;
  void _previous;
  const updated: TextNoteDocument = anchor ? { ...rest, anchor } : rest;
  return commit(state, { ...design, text_notes: notes.map((candidate) => (candidate === note ? updated : candidate)) });
}

function setLabel(state: EditorState, ref: string, label: string): EditorState {
  const design = state.document;
  const node = findNode(design, ref);
  if (!node) return refused(state, { code: "unknown_element", message: `${ref} is not a component in this design`, refs: [ref] });
  const next = label.replace(/\s+/g, " ").trim().slice(0, MAX_LABEL_LENGTH);
  if ((node.label ?? "") === next) return state;
  return commit(state, {
    ...design,
    nodes: design.nodes!.map((candidate) => (candidate === node ? { ...node, label: next } : candidate)),
  });
}

function setPortExternal(state: EditorState, ref: string, external: boolean): EditorState {
  const design = state.document;
  const found = findPort(design, ref);
  if (!found) return refused(state, { code: "unknown_port", message: `${ref} is not a port in this design`, refs: [ref] });
  const instance = lockingInstance(design, found.node.local_id);
  // The lock compares ports, including is_external (core/blocks/lifecycle.py).
  if (instance) return refused(state, lockedRefusal(found.node.local_id, instance.local_id, "change its ports"));
  if ((found.port.is_external ?? false) === external) return state;
  const node = found.node;
  const updated: NodeDocument = {
    ...node,
    ports: node.ports!.map((port) =>
      port === found.port ? { ...port, is_external: external, ...(external ? { dead_end: false } : {}) } : port,
    ),
  };
  return commit(state, { ...design, nodes: design.nodes!.map((candidate) => (candidate === node ? updated : candidate)) });
}

function setNodeTemplate(state: EditorState, ref: string, templateVersionId: string): EditorState {
  const refusal = checkTemplateSwap(state.document, state.library, ref, templateVersionId);
  if (refusal) return refused(state, refusal);
  const node = state.document.nodes!.find((candidate) => candidate.local_id === ref)!;
  if (node.template_version_id === templateVersionId) return state;
  const template = state.library.templates[templateVersionId]!;
  const declared = new Map((template.parameters ?? []).map((parameter) => [parameter.key, parameter]));
  const kept: Record<string, unknown> = {};
  const dropped: string[] = [];
  for (const [key, value] of Object.entries(node.parameters ?? {})) {
    const parameter = declared.get(key);
    const fits =
      parameter !== undefined &&
      (parameter.value_type !== "enum" || (parameter.enum_values ?? []).includes(String(value)));
    if (fits) kept[key] = value;
    else dropped.push(key);
  }
  const previous = new Map((node.ports ?? []).map((port) => [port.local_id, port]));
  const fresh = newNode(state, state.document, templateVersionId, template, { x: 0, y: 0 });
  const updated: NodeDocument = {
    ...node,
    template_version_id: templateVersionId,
    derived_from_template_version_id: null,
    parameters: kept,
    state_set: fresh.state_set ?? null,
    state_transitions: fresh.state_transitions ?? null,
    initial_state: fresh.initial_state ?? null,
    // The new template's ports, keeping what the designer set on each one that survives.
    ports: fresh.ports!.map((port) => {
      const before = previous.get(port.local_id);
      return before
        ? { ...port, is_external: before.is_external ?? port.is_external, ...(before.dead_end ? { dead_end: true } : {}) }
        : port;
    }),
  };
  const next = commit(state, {
    ...state.document,
    nodes: state.document.nodes!.map((candidate) => (candidate === node ? updated : candidate)),
  });
  if (dropped.length === 0) return next;
  const names = dropped.map((key) => key.replace(/_/g, " ")).join(", ");
  return {
    ...next,
    notice: {
      kind: "info",
      message: `${ref} changed type; the new one has no ${names}, so ${dropped.length === 1 ? "that value was" : "those values were"} dropped`,
      refs: [ref],
    },
  };
}

function setLabelSide(state: EditorState, refs: readonly string[], side: LabelSide | undefined): EditorState {
  const wanted = new Set(refs);
  let changed = false;
  const nodes = (state.document.nodes ?? []).map((node) => {
    if (!wanted.has(node.local_id)) return node;
    const position = node.schematic_position ?? { x: 0, y: 0 };
    const current = position.label_side ?? "below";
    const next = side ?? LABEL_SIDES[(LABEL_SIDES.indexOf(current) + 1) % LABEL_SIDES.length]!;
    if (next === current) return node;
    changed = true;
    return { ...node, schematic_position: { ...position, label_side: next } };
  });
  return changed ? commit(state, { ...state.document, nodes }) : state;
}

function setPortDeadEnd(state: EditorState, ref: string, deadEnd: boolean): EditorState {
  const design = state.document;
  const found = findPort(design, ref);
  if (!found) return refused(state, { code: "unknown_port", message: `${ref} is not a port in this design`, refs: [ref] });
  const instance = lockingInstance(design, found.node.local_id);
  // The lock compares ports, including dead_end (core/blocks/lifecycle.py).
  if (instance) return refused(state, lockedRefusal(found.node.local_id, instance.local_id, "change its ports"));
  if ((found.port.dead_end ?? false) === deadEnd) return state;
  const holder = design.edges?.find((edge) => edge.from_port === ref || edge.to_port === ref);
  if (deadEnd && holder) {
    return refused(state, {
      code: "port_occupied",
      message: `${ref} is connected by ${holder.local_id}; remove that line before dead-ending the port`,
      refs: [ref, holder.local_id],
    });
  }
  const node = found.node;
  const updated: NodeDocument = {
    ...node,
    ports: node.ports!.map((port) =>
      port === found.port ? { ...port, dead_end: deadEnd, ...(deadEnd ? { is_external: false } : {}) } : port,
    ),
  };
  return commit(state, { ...design, nodes: design.nodes!.map((candidate) => (candidate === node ? updated : candidate)) });
}

// --- History ------------------------------------------------------------------

function undo(state: EditorState): EditorState {
  const previous = state.undo[state.undo.length - 1];
  if (previous === undefined) return { ...state, drag: null };
  return {
    ...state,
    document: previous,
    undo: state.undo.slice(0, -1),
    redo: [...state.redo, state.document],
    selection: pruneSelection(state.selection, previous),
    drag: null,
    notice: null,
  };
}

function redo(state: EditorState): EditorState {
  const next = state.redo[state.redo.length - 1];
  if (next === undefined) return state;
  return {
    ...state,
    document: next,
    undo: [...state.undo, state.document].slice(-UNDO_LIMIT),
    redo: state.redo.slice(0, -1),
    selection: pruneSelection(state.selection, next),
    drag: null,
    notice: null,
  };
}

// --- The reducer --------------------------------------------------------------

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case "DRAFT_LOADED":
      // A fresh draft from the server: history belongs to the old one.
      return {
        ...state,
        reserved: new Set(action.reserved ?? []),
        document: action.document,
        savedDocument: action.document,
        revision: action.revision,
        selection: pruneSelection(state.selection, action.document),
        collapsed: new Set([...state.collapsed].filter((ref) => liveRefs(action.document).has(ref))),
        drag: null,
        undo: [],
        redo: [],
        notice: null,
      };

    case "DRAFT_ADOPTED":
      return {
        ...state,
        reserved: new Set(action.reserved ?? []),
        document: action.document,
        savedDocument: action.document,
        revision: action.revision,
        selection: pruneSelection(state.selection, action.document),
        drag: null,
        undo: [...state.undo, state.document].slice(-UNDO_LIMIT),
        redo: [],
        notice: null,
      };

    case "DRAFT_SAVED": {
      // The working document may have moved on while the save was in flight;
      // only what was sent is now the saved baseline.
      //
      // Publishing retires identifiers (D29, core/drafts.py), and a retired one
      // is never given back. The editor folds the server's list into the saved
      // baseline, the working document and the history, so neither allocation
      // nor undo reintroduces one. `reserved` is the published base's names the
      // draft lacks: not retired yet, but not free for a new element either.
      const reserved = action.reserved ? new Set(action.reserved) : state.reserved;
      const retired = action.retired ?? [];
      const known = new Set(action.document.retired_local_ids ?? []);
      if (retired.every((id) => known.has(id))) {
        return { ...state, savedDocument: action.document, revision: action.revision, reserved };
      }
      const withRetired = (document: DesignDocument): DesignDocument => {
        const merged = [...new Set([...(document.retired_local_ids ?? []), ...retired])].sort();
        return merged.length === (document.retired_local_ids ?? []).length
          ? document
          : { ...document, retired_local_ids: merged };
      };
      const saved = withRetired(action.document);
      return {
        ...state,
        savedDocument: saved,
        // Unchanged since the save: stays identical to the baseline, so it
        // does not read as an unsaved edit.
        document: state.document === action.document ? saved : withRetired(state.document),
        undo: state.undo.map(withRetired),
        redo: state.redo.map(withRetired),
        revision: action.revision,
        reserved,
      };
    }

    case "CLEAR_NOTICE":
      return state.notice === null ? state : { ...state, notice: null };

    case "SET_DESIGN_KIND": {
      if (action.kind === state.designKind) return state;
      const fits = (id: string | null) => {
        const template = id ? state.library.templates[id] : undefined;
        return template !== undefined && servesKind(template, action.kind);
      };
      return {
        ...state,
        designKind: action.kind,
        connectorTemplateId: fits(state.connectorTemplateId)
          ? state.connectorTemplateId
          : defaultConnector(state.library, action.kind),
        junctionTemplateId: fits(state.junctionTemplateId)
          ? state.junctionTemplateId
          : defaultJunction(state.library, action.kind),
        airLineTemplateId: fits(state.airLineTemplateId) ? state.airLineTemplateId : defaultAirLine(state.library, action.kind),
      };
    }

    case "LIBRARY_LOADED": {
      const keep = state.connectorTemplateId && action.library.templates[state.connectorTemplateId];
      return {
        ...state,
        library: action.library,
        connectorTemplateId: keep ? state.connectorTemplateId : defaultConnector(action.library, state.designKind),
        junctionTemplateId:
          state.junctionTemplateId && action.library.templates[state.junctionTemplateId]
            ? state.junctionTemplateId
            : defaultJunction(action.library, state.designKind),
        airLineTemplateId:
          state.airLineTemplateId && action.library.templates[state.airLineTemplateId]
            ? state.airLineTemplateId
            : defaultAirLine(action.library, state.designKind),
      };
    }

    case "SET_TOOL":
      return { ...state, tool: action.tool, drag: null, notice: null };

    case "SET_CONNECTOR_TEMPLATE":
      return { ...state, connectorTemplateId: action.templateVersionId };

    case "SET_AIR_LINE_TEMPLATE":
      return state.library.templates[action.templateVersionId]?.role === "air_line"
        ? { ...state, airLineTemplateId: action.templateVersionId }
        : state;

    case "SET_TEMPLATE_ENTITIES":
      return { ...state, templateEntities: action.entities };

    case "SET_USER_DEFAULTS":
      return { ...state, userDefaults: action.defaults };

    case "SET_PLACEMENT_DEFAULTS": {
      const { placement_defaults: previous, ...rest } = state.document;
      // Saving the same table is not an edit: no undo step, nothing to save.
      if (JSON.stringify(previous ?? {}) === JSON.stringify(action.defaults)) return state;
      const document: DesignDocument =
        Object.keys(action.defaults).length > 0 ? { ...rest, placement_defaults: { ...action.defaults } } : rest;
      return commit(state, document);
    }

    case "SET_JUNCTION_TEMPLATE":
      return state.library.templates[action.templateVersionId]
        ? { ...state, junctionTemplateId: action.templateVersionId }
        : state;

    case "BRANCH_EDGE":
      return branchEdge(state, action.edge, action.from, action.at);

    case "PLACE_NODE":
      return placeNode(state, action.templateVersionId, action.at, action.intoEdge);

    case "BEGIN_CONNECT":
      return beginConnect(state, action.port);

    case "HOVER_PORT":
      if (state.drag?.kind !== "connect") return state;
      return { ...state, drag: { ...state.drag, hover: action.port } };

    case "COMPLETE_CONNECT":
      return completeConnect(
        state,
        action.port ?? (state.drag?.kind === "connect" ? state.drag.hover : null),
      );

    case "CANCEL":
      if (state.drag?.kind === "move") {
        return { ...state, document: state.drag.origin, drag: null, notice: null };
      }
      return { ...state, drag: null, notice: null };

    case "SELECT":
      return select(state, action.refs, action.mode ?? "replace");

    case "SELECT_ALL": {
      // Collapsed blocks as one element; their hidden members are not drawn.
      const hidden = new Set(
        (state.document.group_instances ?? [])
          .filter((instance) => !instance.detached && state.collapsed.has(instance.local_id))
          .flatMap((instance) => instance.member_local_ids ?? []),
      );
      const refs = [
        ...(state.document.nodes ?? []).map((node) => node.local_id),
        ...(state.document.edges ?? []).map((edge) => edge.local_id),
        ...(state.annotations.notes ? (state.document.text_notes ?? []).map((note) => note.local_id) : []),
      ].filter((ref) => !hidden.has(ref));
      const blocks = [...state.collapsed].filter((ref) => liveRefs(state.document).has(ref));
      // An isolated view selects what it shows, and notes on it.
      const side = isolatedSide(state);
      const notesShown = new Set(
        (state.document.text_notes ?? []).filter((note) => !side || (note.anchor && side.has(note.anchor))).map((note) => note.local_id),
      );
      const all = [...refs, ...blocks].filter((ref) => !side || side.has(ref) || notesShown.has(ref));
      return select(state, all, "replace");
    }

    case "CLEAR_SELECTION":
      return { ...state, selection: new Set(), notice: null };

    case "BOX_SELECT":
      return boxSelect(state, action.box, action.mode ?? "replace");

    case "BEGIN_MOVE":
      return { ...state, drag: { kind: "move", origin: state.document }, notice: null };

    case "MOVE_SELECTION":
      if (state.drag?.kind === "move") {
        // Offsets are from where the drag began, so a pointer that wanders
        // and returns leaves nothing behind.
        return { ...state, document: moveNodes(state, state.drag.origin, action.dx, action.dy) };
      }
      return commit(state, moveNodes(state, state.document, action.dx, action.dy));

    case "END_MOVE": {
      if (state.drag?.kind !== "move") return state;
      const origin = state.drag.origin;
      let document = state.document;
      let notice: Notice | null = state.notice;
      if (action.intoEdge && action.at) {
        const spliced = spliceMoved(state, action.intoEdge, action.at);
        document = spliced.document;
        notice = spliced.notice;
      }
      if (origin === document) return { ...state, drag: null, notice };
      // The move, and any splice it ended in, is one undo step.
      return {
        ...state,
        document,
        drag: null,
        notice,
        undo: [...state.undo, origin].slice(-UNDO_LIMIT),
        redo: [],
      };
    }

    case "ROTATE_SELECTION":
      return orientSelection(state, { kind: "rotate", quarterTurns: action.quarterTurns });

    case "MIRROR_SELECTION":
      return orientSelection(state, { kind: "flip", axis: action.axis });

    case "DELETE_SELECTION":
      return deleteSelection(state);

    case "EDIT_PARAMETER":
      return editParameter(state, action.ref, action.key, action.value);

    case "EDIT_PARAMETERS":
      return editParameters(state, action.refs, action.key, action.value);

    case "SET_GRID": {
      const spacing = action.spacing ?? state.grid.spacing;
      if (!(spacing > 0) || !Number.isFinite(spacing)) return state;
      return {
        ...state,
        grid: {
          spacing,
          snap: action.snap ?? state.grid.snap,
          visible: action.visible ?? state.grid.visible,
        },
      };
    }

    case "SET_COLLAPSED": {
      const collapsed = new Set(state.collapsed);
      if (action.collapsed) collapsed.add(action.instance);
      else collapsed.delete(action.instance);
      // Hidden members cannot stay selected; the block stands in for them.
      let selection = state.selection;
      if (action.collapsed) {
        const members = new Set(
          state.document.group_instances?.find((i) => i.local_id === action.instance)?.member_local_ids ?? [],
        );
        if ([...selection].some((ref) => members.has(ref))) {
          selection = new Set([...[...selection].filter((ref) => !members.has(ref)), action.instance]);
        }
      }
      return { ...state, collapsed, selection };
    }

    case "SET_ANNOTATIONS": {
      const annotations = {
        labels: action.labels ?? state.annotations.labels,
        dimensions: action.dimensions ?? state.annotations.dimensions,
        fluidLines: action.fluidLines ?? state.annotations.fluidLines,
        airLines: action.airLines ?? state.annotations.airLines,
        notes: action.notes ?? state.annotations.notes,
        wash: action.wash ?? state.annotations.wash,
        isolate: false,
      };
      // Isolating means one side shown on its own: nothing to isolate without a wash.
      annotations.isolate = annotations.wash !== "none" && (action.isolate ?? state.annotations.isolate ?? false);
      // A hidden line stays out of the selection: Delete should never act on
      // something the designer cannot see.
      const shownSide = isolatedSide(state, annotations);
      const hidden = new Set([
        ...(state.document.edges ?? []).filter((edge) => !lineShown(annotations, edge.type)).map((edge) => edge.local_id),
        ...(annotations.notes ? [] : (state.document.text_notes ?? []).map((note) => note.local_id)),
        ...(shownSide ? [...liveRefs(state.document)].filter((ref) => !shownSide.has(ref) && !isNote(state, ref)) : []),
      ]);
      const selection = [...state.selection].some((ref) => hidden.has(ref))
        ? new Set([...state.selection].filter((ref) => !hidden.has(ref)))
        : state.selection;
      return { ...state, annotations, selection };
    }

    case "SET_VIEWPORT": {
      const { x, y, zoom } = action.viewport;
      if (![x, y, zoom].every(Number.isFinite)) return state;
      return { ...state, viewport: { x, y, zoom: clampZoom(zoom) } };
    }

    case "SET_EDGE_TEMPLATE":
      return setEdgeTemplate(state, action.ref, action.templateVersionId);

    case "SET_EDGE_WAYPOINTS":
      return setEdgeWaypoints(state, action.ref, action.waypoints);

    case "RECONNECT_EDGE":
      return reconnectEdge(state, action.ref, action.end, action.port);

    case "SET_LABEL":
      return setLabel(state, action.ref, action.label);
    case "MOUNT_ON_LINE": {
      if (state.designKind !== "tubing_set") {
        return refused(state, { code: "cannot_mount", message: "only a tubing set mounts parts on its lines", refs: [action.ref] });
      }
      const result = mountOnLine(state.document, state.library, action.ref);
      if ("refusal" in result) return refused(state, { code: "cannot_mount", message: result.refusal, refs: [action.ref] });
      return {
        ...commit(state, result.document, { selection: new Set([action.ref]) }),
        notice: { kind: "info", message: result.message, refs: [action.ref] },
      };
    }
    case "SET_MOUNT_DISTANCE": {
      const node = findNode(state.document, action.ref);
      if (!node?.mount || !Number.isFinite(action.distance) || action.distance < 0) return state;
      if (node.mount.distance === action.distance) return state;
      return commit(state, {
        ...state.document,
        // The drawing follows the distance again until the part is next dragged.
        nodes: state.document.nodes!.map((candidate) =>
          candidate === node ? { ...node, mount: { edge: node.mount!.edge, distance: action.distance, along: null } } : candidate,
        ),
      });
    }
    case "ADD_NOTE":
      return addNote(state, action.at, action.text, action.anchor, action.size);
    case "SET_NOTE_TEXT":
      return setNoteText(state, action.ref, action.text, action.size);
    case "SET_NOTE_SIZE":
      return setNoteSize(state, action.ref, action.size);
    case "SET_NOTE_ANCHOR":
      return setNoteAnchor(state, action.ref, action.anchor);
    case "SET_COLLAPSED_POSITION": {
      const instances = state.document.group_instances ?? [];
      const target = instances.find((instance) => instance.local_id === action.instance);
      if (!target || (target.collapsed_position ?? null) === action.position) return state;
      const { collapsed_position: _previous, ...rest } = target;
      const updated = action.position ? { ...rest, collapsed_position: action.position } : rest;
      return commit(state, {
        ...state.document,
        group_instances: instances.map((instance) => (instance === target ? updated : instance)),
      });
    }

    case "SET_NODE_TEMPLATE":
      return setNodeTemplate(state, action.ref, action.templateVersionId);
    case "SET_LABEL_SIDE":
      return setLabelSide(state, action.refs ?? [...state.selection], action.side);

    case "SET_PORT_EXTERNAL":
      return setPortExternal(state, action.ref, action.external);

    case "SET_PORT_DEAD_END":
      return setPortDeadEnd(state, action.ref, action.deadEnd);

    case "COPY_SELECTION": {
      const content = copySelection(withDrawnPositions(state), state.selection);
      return content ? { ...state, clipboard: content, notice: null } : state;
    }

    case "CUT_SELECTION": {
      const content = copySelection(withDrawnPositions(state), state.selection);
      if (!content) return state;
      const deleted = deleteSelection(state);
      // Nothing was deleted (a locked member): nothing was cut either.
      return deleted.document === state.document ? deleted : { ...deleted, clipboard: content };
    }

    case "PASTE":
      return paste(state, action.at);

    case "UNDO":
      return undo(state);

    case "REDO":
      return redo(state);
  }
}
